import React, { useMemo } from 'react';
import { Box, Grid, Typography, Button, Card, CardContent, Avatar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { colors } from './Theme/WebTheme';
import { FONT_FAMILY } from '../common/sharedFunctions';
import MobileSlider from './MobileSlider';
import PersonIcon from '@mui/icons-material/Person';

const OffersSection = React.memo(() => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const cartypes = useSelector(state => state.cartypes);
  const usersdata = useSelector(state => state.usersdata);

  const langCode = useMemo(() => 
    (i18n?.language ? i18n.language.slice(0, 2).toLowerCase() : 'fr'),
    [i18n?.language]
  );

  // Get drivers with their car types and photos
  const getDriversForCarType = useMemo(() => {
    if (!usersdata?.users) return {};
    
    const driversByCarType = {};
    
    usersdata.users.forEach(user => {
      if (user.usertype === 'driver' && user.approved && user.carType) {
        const carTypeName = user.carType.toLowerCase();
        if (!driversByCarType[carTypeName]) {
          driversByCarType[carTypeName] = [];
        }
        
        // Priority to drivers with car_image
        driversByCarType[carTypeName].push({
          firstName: user.firstName || 'Chauffeur',
          vehicleMake: user.vehicleMake,
          vehicleModel: user.vehicleModel,
          carImage: user.car_image,
          profilePic: user.profile_image,
          hasPhoto: !!user.car_image
        });
      }
    });
    
    // Sort each car type: drivers with photos first, then shuffle
    Object.keys(driversByCarType).forEach(carType => {
      const drivers = driversByCarType[carType];
      const withPhotos = drivers.filter(d => d.hasPhoto);
      const withoutPhotos = drivers.filter(d => !d.hasPhoto);
      
      // Shuffle both arrays
      const shuffleArray = (arr) => {
        const shuffled = [...arr];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
      };
      
      driversByCarType[carType] = [...shuffleArray(withPhotos), ...shuffleArray(withoutPhotos)].slice(0, 2);
    });
    
    return driversByCarType;
  }, [usersdata?.users]);

  const offersContent = useMemo(() => ({
    fr: {
      title: 'Notre Flotte Premium :\nConfort & Élégance',
      subtitle: 'Du véhicule économique à la berline de prestige, choisissez le confort qui vous ressemble. Tous nos véhicules sont récents, climatisés et impeccablement entretenus.',
      bookNow: 'Réserver ce véhicule',
      person: 'Pers'
    },
    en: {
      title: 'Our Premium Fleet:\nComfort & Elegance',
      subtitle: 'From economical vehicles to prestige sedans, choose the comfort that suits you. All our vehicles are recent, air-conditioned and impeccably maintained.',
      bookNow: 'Book this vehicle',
      person: 'Pers'
    }
  }), []);

  const content = useMemo(() => 
    offersContent[langCode] || offersContent.fr,
    [offersContent, langCode]
  );

  // Map cartypes to offers with default values
  const offers = useMemo(() => {
    if (!cartypes?.cars) return [];
    
    return cartypes.cars
      .sort((a, b) => (a.pos || 0) - (b.pos || 0))
      .slice(0, 4)
      .map(car => {
        const carName = (car.name || 'Vehicle').toLowerCase();
        
        // Déterminer la capacité selon le type de véhicule
        let persons = car.max_passengers || 4;
        if (carName.includes('van')) {
          persons = 7;
        } else if (carName.includes('eco')) {
          persons = 3;
        } else if (carName.includes('confort')) {
          persons = 4;
        }
        
        return {
          name: car.name || 'Vehicle',
          image: car.image,
          persons: persons,
          luggage: car.max_luggage || 2,
          carData: car
        };
      });
  }, [cartypes]);

  const handleBooking = (carData) => {
    navigate('/addbookings', { state: { carData } });
  };

  return (
    <Box sx={{ 
      py: { xs: 6, md: 10 }, 
      px: { xs: 2, md: 4 },
      backgroundColor: '#0a0a0a',
      position: 'relative'
    }}>
      <Box sx={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontFamily: FONT_FAMILY,
              fontWeight: 700,
              fontSize: { xs: '32px', md: '48px' },
              color: '#fff',
              mb: 2
            }}
          >
            {content.title}
          </Typography>
          <Typography 
            sx={{ 
              fontFamily: FONT_FAMILY,
              fontSize: { xs: '16px', md: '18px' },
              color: 'rgba(255,255,255,0.7)',
              maxWidth: '800px',
              margin: '0 auto',
              lineHeight: 1.6
            }}
          >
            {content.subtitle}
          </Typography>
        </Box>

        {/* Offers Grid - Desktop */}
        <Grid container spacing={3} sx={{ justifyContent: 'center', display: { xs: 'none', sm: 'flex' } }}>
          {offers.map((offer, index) => {
            const carNameLower = offer.name.toLowerCase();
            const drivers = getDriversForCarType[carNameLower] || [];
            
            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card 
                  sx={{ 
                    height: '100%',
                    backgroundColor: index % 2 === 0 ? 'rgba(212, 175, 55, 0.08)' : 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${index % 2 === 0 ? colors.Button_Primary : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: '20px',
                    transition: 'all 0.3s ease',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: `0 16px 48px rgba(212, 175, 55, 0.4)`,
                      borderColor: colors.Button_Primary
                    }
                  }}
                >
                  {/* Car Image */}
                  {offer.image && (
                    <Box sx={{ 
                      height: 180,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.3)',
                      p: 2
                    }}>
                      <img 
                        src={offer.image} 
                        alt={offer.name}
                        style={{ 
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    </Box>
                  )}

                  <CardContent sx={{ p: 3 }}>
                    {/* Car Name */}
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontFamily: FONT_FAMILY,
                        fontWeight: 700,
                        color: '#fff',
                        mb: 2,
                        textAlign: 'center',
                        fontSize: '22px'
                      }}
                    >
                      {offer.name}
                    </Typography>

                    {/* Drivers Section */}
                    {drivers.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1, 
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}>
                          {drivers.map((driver, driverIndex) => (
                            <Box 
                              key={driverIndex}
                              sx={{ 
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              <Avatar
                                src={driver.carImage || offer.image}
                                sx={{ 
                                  width: 40, 
                                  height: 40,
                                  border: `2px solid ${colors.Button_Primary}`,
                                  bgcolor: 'rgba(212, 175, 55, 0.2)'
                                }}
                              >
                                {!driver.carImage && <PersonIcon sx={{ fontSize: 24 }} />}
                              </Avatar>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography 
                                  sx={{ 
                                    fontSize: '11px',
                                    color: colors.Button_Primary,
                                    fontFamily: FONT_FAMILY,
                                    fontWeight: 600,
                                    lineHeight: 1.2
                                  }}
                                >
                                  {driver.firstName}
                                </Typography>
                                {(driver.vehicleMake || driver.vehicleModel) && (
                                  <Typography 
                                    sx={{ 
                                      fontSize: '9px',
                                      color: colors.Button_Primary,
                                      fontFamily: FONT_FAMILY,
                                      fontWeight: 400,
                                      lineHeight: 1.2,
                                      opacity: 0.8
                                    }}
                                  >
                                    {driver.vehicleMake} {driver.vehicleModel}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Capacity Info */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center',
                      mb: 3
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PersonIcon sx={{ color: colors.Button_Primary, fontSize: 20 }} />
                        <Typography sx={{ 
                          color: '#fff', 
                          fontFamily: FONT_FAMILY,
                          fontSize: '15px',
                          fontWeight: 600
                        }}>
                          {offer.persons} {content.person}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Book Button - Only show if drivers exist */}
                    {drivers.length > 0 && (
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => handleBooking(offer.carData)}
                        sx={{
                          backgroundColor: colors.Button_Primary,
                          color: '#000',
                          fontFamily: FONT_FAMILY,
                          fontWeight: 700,
                          textTransform: 'none',
                          borderRadius: '12px',
                          py: 1.5,
                          fontSize: '16px',
                          '&:hover': {
                            backgroundColor: '#e5c158',
                            transform: 'scale(1.02)'
                          }
                        }}
                      >
                        {content.bookNow}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Mobile Slider */}
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          <MobileSlider spacing={3}>
            {offers.map((offer, index) => {
              const carNameLower = offer.name.toLowerCase();
              const drivers = getDriversForCarType[carNameLower] || [];
              
              return (
                <Card 
                  key={index}
                  sx={{ 
                    height: '100%',
                    backgroundColor: index % 2 === 0 ? 'rgba(212, 175, 55, 0.08)' : 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${index % 2 === 0 ? colors.Button_Primary : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: '20px',
                    overflow: 'hidden'
                  }}
                >
                  {/* Car Image */}
                  {offer.image && (
                    <Box sx={{ 
                      height: 180,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.3)',
                      p: 2
                    }}>
                      <img 
                        src={offer.image} 
                        alt={offer.name}
                        style={{ 
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    </Box>
                  )}

                  <CardContent sx={{ p: 3 }}>
                    {/* Car Name */}
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontFamily: FONT_FAMILY,
                        fontWeight: 700,
                        color: '#fff',
                        mb: 2,
                        textAlign: 'center',
                        fontSize: '22px'
                      }}
                    >
                      {offer.name}
                    </Typography>

                    {/* Drivers Section */}
                    {drivers.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1, 
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}>
                          {drivers.map((driver, driverIndex) => (
                            <Box 
                              key={driverIndex}
                              sx={{ 
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              <Avatar
                                src={driver.carImage || offer.image}
                                sx={{ 
                                  width: 40, 
                                  height: 40,
                                  border: `2px solid ${colors.Button_Primary}`,
                                  bgcolor: 'rgba(212, 175, 55, 0.2)'
                                }}
                              >
                                {!driver.carImage && <PersonIcon sx={{ fontSize: 24 }} />}
                              </Avatar>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography 
                                  sx={{ 
                                    fontSize: '11px',
                                    color: colors.Button_Primary,
                                    fontFamily: FONT_FAMILY,
                                    fontWeight: 600,
                                    lineHeight: 1.2
                                  }}
                                >
                                  {driver.firstName}
                                </Typography>
                                {(driver.vehicleMake || driver.vehicleModel) && (
                                  <Typography 
                                    sx={{ 
                                      fontSize: '9px',
                                      color: colors.Button_Primary,
                                      fontFamily: FONT_FAMILY,
                                      fontWeight: 400,
                                      lineHeight: 1.2,
                                      opacity: 0.8
                                    }}
                                  >
                                    {driver.vehicleMake} {driver.vehicleModel}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Capacity Info */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center',
                      mb: 3
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PersonIcon sx={{ color: colors.Button_Primary, fontSize: 20 }} />
                        <Typography sx={{ 
                          color: '#fff', 
                          fontFamily: FONT_FAMILY,
                          fontSize: '15px',
                          fontWeight: 600
                        }}>
                          {offer.persons} {content.person}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Book Button - Only show if drivers exist */}
                    {drivers.length > 0 && (
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => handleBooking(offer.carData)}
                        sx={{
                          backgroundColor: colors.Button_Primary,
                          color: '#000',
                          fontFamily: FONT_FAMILY,
                          fontWeight: 700,
                          textTransform: 'none',
                          borderRadius: '12px',
                          py: 1.5,
                          fontSize: '16px',
                          '&:hover': {
                            backgroundColor: '#e5c158',
                            transform: 'scale(1.02)'
                          }
                        }}
                      >
                        {content.bookNow}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </MobileSlider>
        </Box>
      </Box>
    </Box>
  );
});

export default OffersSection;
